(() => {
  'use strict';

  /* ---------- Helpers ---------- */
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(cents / 100);
    } catch {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  /* ---------- Cart API ---------- */
  const Cart = {
    async add(items) {
      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error((await res.json()).description || window.themeStrings.cartError);
      return res.json();
    },
    async changeLine(line, quantity) {
      const res = await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error(window.themeStrings.cartError);
      return res.json();
    },
    async refreshDrawer() {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await res.json();
      const html = data['cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = qs('[data-cart-drawer]', doc);
      const current = qs('[data-cart-drawer]');
      if (fresh && current) {
        const wasOpen = current.classList.contains('is-open');
        current.replaceWith(fresh);
        if (wasOpen) fresh.classList.add('is-open');
        bindCartDrawer();
      }
      const countRes = await fetch('/cart.js');
      const cart = await countRes.json();
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
    }
  };

  const openCartDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (drawer) {
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  };
  const closeCartDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (drawer) {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  function bindCartDrawer() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer || drawer.dataset.bound) return;
    drawer.dataset.bound = '1';
    qsa('[data-cart-close]', drawer).forEach((btn) => btn.addEventListener('click', closeCartDrawer));
    qsa('.cart-item', drawer).forEach((item) => {
      const line = Number(item.dataset.line);
      const qtyValue = qs('.qty__value', item);
      const lockItem = () => qsa('button', item).forEach((b) => (b.disabled = true));
      qsa('[data-qty-change]', item).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const next = Number(qtyValue.textContent) + Number(btn.dataset.qtyChange);
          lockItem();
          await Cart.changeLine(line, Math.max(0, next)).catch(() => {});
          Cart.refreshDrawer();
        });
      });
      const removeBtn = qs('[data-line-remove]', item);
      if (removeBtn) {
        removeBtn.addEventListener('click', async () => {
          lockItem();
          await Cart.changeLine(line, 0).catch(() => {});
          Cart.refreshDrawer();
        });
      }
    });
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) {
      e.preventDefault();
      openCartDrawer();
    }
  });

  /* ---------- Mobile nav ---------- */
  function bindMobileNav() {
    const nav = qs('[data-mobile-nav]');
    const openBtn = qs('[data-mobile-nav-open]');
    if (!nav || !openBtn || openBtn.dataset.bound) return;
    openBtn.dataset.bound = '1';
    openBtn.addEventListener('click', () => {
      nav.hidden = false;
      nav.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
    });
    qsa('[data-mobile-nav-close]', nav).forEach((btn) =>
      btn.addEventListener('click', () => {
        nav.classList.remove('is-open');
        nav.hidden = true;
        openBtn.setAttribute('aria-expanded', 'false');
      })
    );
  }

  /* ---------- Countdown ---------- */
  function bindCountdowns() {
    qsa('[data-countdown]').forEach((el) => {
      const hours = Number(el.dataset.countdownHours || 3);
      const key = 'countdown-' + el.dataset.countdownKey;
      const out = qs('[data-countdown-time]', el);
      if (!out) return;

      let target = Number(localStorage.getItem(key));
      if (!target || target < Date.now()) {
        target = Date.now() + hours * 3600 * 1000;
        localStorage.setItem(key, String(target));
      }

      const tick = () => {
        let diff = Math.max(0, target - Date.now());
        if (diff === 0) {
          target = Date.now() + hours * 3600 * 1000;
          localStorage.setItem(key, String(target));
          diff = target - Date.now();
        }
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        out.textContent = [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
      };
      tick();
      if (el._countdownTimer) clearInterval(el._countdownTimer);
      el._countdownTimer = setInterval(tick, 1000);
    });
  }

  /* ---------- Delivery timeline dates ---------- */
  function bindTimelines() {
    const fmt = (d) => {
      const month = d.toLocaleDateString('en-US', { month: 'short' });
      const day = d.getDate();
      const suffix =
        day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
      return `${month} ${day}${suffix}`;
    };
    qsa('[data-date-from]').forEach((el) => {
      const from = new Date();
      from.setDate(from.getDate() + Number(el.dataset.dateFrom));
      const to = new Date();
      to.setDate(to.getDate() + Number(el.dataset.dateTo));
      el.textContent = el.dataset.dateFrom === el.dataset.dateTo ? fmt(from) : `${fmt(from)} - ${fmt(to)}`;
    });
  }

  /* ---------- Product landing ---------- */
  function bindProductLanding() {
    qsa('[data-product-landing]').forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = '1';
      const currency = root.dataset.currency;
      const packIndex = Number(root.dataset.packIndex);
      const colorIndex = Number(root.dataset.colorIndex);
      const protectionVariant = root.dataset.protectionVariant;
      const form = qs('[data-product-form], form[action*="/cart/add"]', root);
      const variantInput = qs('[data-variant-id]', root);
      const variantsJson = qs('[data-variants-json]', root);
      const variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];
      const atcButton = qs('[data-atc-button]', root);
      const stickyBar = qs('[data-sticky-atc]', root.parentElement || document);
      const stickyPrice = stickyBar ? qs('[data-sticky-price]', stickyBar) : null;

      const state = { pack: null, colors: {}, protection: false };

      const selectedBundle = () => qs('.bundle.is-selected[data-bundle]', root);

      const resolveVariant = () => {
        const bundle = selectedBundle();
        if (!bundle) {
          const currentId = variantInput ? Number(variantInput.value) : null;
          return (
            variants.find((v) => v.id === currentId) ||
            variants.find((v) => v.available) ||
            variants[0] ||
            null
          );
        }
        const pack = bundle.dataset.packValue;
        const activeSwatch = qs('.swatch.is-active', bundle);
        const color = activeSwatch ? activeSwatch.dataset.colorValue : null;
        const match = (requireColor) =>
          variants.find((v) => {
            const opts = [v.option1, v.option2, v.option3];
            const packOk = packIndex < 0 || opts[packIndex] === pack;
            const colorOk = !requireColor || colorIndex < 0 || !color || opts[colorIndex] === color;
            return packOk && colorOk;
          });
        return match(true) || match(false) || null;
      };

      const sync = () => {
        const v = resolveVariant();
        if (!v) {
          if (atcButton) {
            atcButton.disabled = true;
            atcButton.textContent = window.themeStrings.soldOut;
          }
          return;
        }
        if (variantInput) variantInput.value = v.id;
        if (stickyPrice) {
          let html = formatMoney(v.price, currency);
          if (v.compare_at_price && v.compare_at_price > v.price) {
            html += ` <s>${formatMoney(v.compare_at_price, currency)}</s>`;
          }
          stickyPrice.innerHTML = html;
        }
        if (atcButton) {
          atcButton.disabled = v.available === false;
          atcButton.textContent = v.available === false ? window.themeStrings.soldOut : window.themeStrings.addToCart;
        }
      };

      qsa('[data-bundle]', root).forEach((bundle) => {
        const select = () => {
          qsa('[data-bundle]', root).forEach((b) => {
            b.classList.toggle('is-selected', b === bundle);
            b.setAttribute('aria-checked', b === bundle ? 'true' : 'false');
            if (b !== bundle) {
              const cb = qs('[data-protection-checkbox]', b);
              if (cb) cb.checked = false;
            }
          });
          sync();
        };
        bundle.addEventListener('click', (e) => {
          if (e.target.closest('.swatch') || e.target.closest('.protection')) return;
          select();
        });
        bundle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            select();
          }
        });
        qsa('.swatch', bundle).forEach((swatch) => {
          swatch.addEventListener('click', () => {
            qsa('.swatch', bundle).forEach((s) => {
              s.classList.toggle('is-active', s === swatch);
              s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false');
            });
            sync();
          });
        });
      });

      const addSelection = async () => {
        const v = resolveVariant();
        if (!v || v.available === false) return;
        const items = [{ id: v.id, quantity: 1 }];
        const scope = selectedBundle() || root;
        const protection = qs('[data-protection-checkbox]:checked', scope);
        if (protection && protectionVariant) {
          items.push({ id: Number(protectionVariant), quantity: 1 });
        }
        if (atcButton) atcButton.disabled = true;
        try {
          await Cart.add(items);
          await Cart.refreshDrawer();
          openCartDrawer();
        } catch (err) {
          alert(err.message || window.themeStrings.cartError);
        } finally {
          if (atcButton) atcButton.disabled = false;
        }
      };

      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          addSelection();
        });
      }

      if (stickyBar) {
        const stickyAdd = qs('[data-sticky-add]', stickyBar);
        if (stickyAdd) stickyAdd.addEventListener('click', addSelection);
        if (atcButton && 'IntersectionObserver' in window) {
          new IntersectionObserver(
            (entries) => {
              const visible = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
              stickyBar.classList.toggle('is-visible', visible);
              document.body.style.paddingBottom = visible ? stickyBar.offsetHeight + 'px' : '';
            },
            { threshold: 0 }
          ).observe(atcButton);
        }
      }

      /* Gallery */
      const gallery = qs('[data-gallery]', root);
      if (gallery) {
        const slides = qsa('[data-gallery-slide]', gallery);
        const thumbs = qsa('[data-gallery-thumb]', gallery);
        let index = 0;
        const show = (i) => {
          index = (i + slides.length) % slides.length;
          slides.forEach((s, j) => s.classList.toggle('is-active', j === index));
          thumbs.forEach((t, j) => t.classList.toggle('is-active', j === index));
        };
        thumbs.forEach((t) => t.addEventListener('click', () => show(Number(t.dataset.galleryThumb))));
        const prev = qs('[data-gallery-prev]', gallery);
        const next = qs('[data-gallery-next]', gallery);
        if (prev) prev.addEventListener('click', () => show(index - 1));
        if (next) next.addEventListener('click', () => show(index + 1));
      }

      /* Mini quotes carousel */
      const quotesWrap = qs('[data-mini-quotes]', root);
      if (quotesWrap) {
        const quotes = qsa('[data-mini-quote]', quotesWrap);
        const dots = qsa('[data-mini-dot]', quotesWrap);
        if (quotes.length > 1) {
          let qi = 0;
          const showQuote = (i) => {
            qi = (i + quotes.length) % quotes.length;
            quotes.forEach((quote, j) => quote.classList.toggle('is-active', j === qi));
            dots.forEach((d, j) => d.classList.toggle('is-active', j === qi));
          };
          dots.forEach((d) => d.addEventListener('click', () => showQuote(Number(d.dataset.miniDot))));
          if (quotesWrap._timer) clearInterval(quotesWrap._timer);
          quotesWrap._timer = setInterval(() => showQuote(qi + 1), 5000);
        }
      }

      sync();
    });
  }

  /* ---------- Before / after slider ---------- */
  function bindBeforeAfter() {
    qsa('[data-before-after]').forEach((wrap) => {
      const range = qs('[data-ba-range]', wrap);
      if (!range || wrap.dataset.bound) return;
      wrap.dataset.bound = '1';
      range.addEventListener('input', () => {
        wrap.style.setProperty('--ba-pos', range.value + '%');
      });
    });
  }

  /* ---------- Customer results concern filter ---------- */
  function bindResultsHubs() {
    qsa('[data-results-hub]').forEach((hub) => {
      if (hub.dataset.bound) return;
      hub.dataset.bound = '1';
      const filters = qsa('[data-results-filter]', hub);
      const cards = qsa('[data-result-card]', hub);
      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          const concern = filter.dataset.resultsFilter;
          filters.forEach((item) => {
            const active = item === filter;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          cards.forEach((card) => {
            const matches = concern === 'all' || card.dataset.resultConcerns.split(' ').includes(concern);
            card.hidden = !matches;
          });
        });
      });
    });
  }

  /* ---------- Reviews sort ---------- */
  function bindReviewsSort() {
    qsa('[data-reviews-sort]').forEach((select) => {
      const list = select.closest('#reviews, .section-pad')?.querySelector('[data-reviews-list]');
      if (!list || select.dataset.bound) return;
      select.dataset.bound = '1';
      const original = qsa('[data-review]', list);
      select.addEventListener('change', () => {
        let sorted = [...original];
        if (select.value === 'highest') sorted.sort((a, b) => b.dataset.rating - a.dataset.rating);
        if (select.value === 'lowest') sorted.sort((a, b) => a.dataset.rating - b.dataset.rating);
        sorted.forEach((el) => list.appendChild(el));
      });
    });
  }

  /* ---------- Promo popup ---------- */
  function bindPromoPopup() {
    const popup = qs('[data-promo-popup]');
    if (!popup || popup.dataset.bound) return;
    popup.dataset.bound = '1';
    const key = 'promo-popup-shown';
    if (sessionStorage.getItem(key)) return;
    setTimeout(() => {
      popup.classList.add('is-open');
      sessionStorage.setItem(key, '1');
    }, Number(popup.dataset.promoDelay || 8) * 1000);
    qsa('[data-promo-close]', popup).forEach((btn) =>
      btn.addEventListener('click', () => popup.classList.remove('is-open'))
    );
  }

  /* ---------- Init ---------- */
  const init = () => {
    bindCartDrawer();
    bindMobileNav();
    bindCountdowns();
    bindTimelines();
    bindProductLanding();
    bindBeforeAfter();
    bindResultsHubs();
    bindReviewsSort();
    bindPromoPopup();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
